import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import './BlogPost.css';
import { teamMembers } from './TeamMemberProfile';

export interface BlogPostData {
    id: string;
    title: string;
    author: string;
    authorId: string;        // matches TeamMemberProfile id for linking
    date: string;            // e.g. "April 11, 2026"
    category: string;        // e.g. "Consumer Rights"
    summary: string;         // short blurb shown on the Blog listing page
    image?: string;          // optional hero image shown on the post page (e.g. "/images/business.jpg")
    body: string[];          // each string is a paragraph
}

export const blogPosts: BlogPostData[] = [
    {
        id: 'maaco-settlement', // website url for the blog post
        title: 'Client Obtains Favorable Settlement Following Maaco Auto Body Temecula’s Defective Painting Services',
        author: 'Samuel Dickinson',
        authorId: 'sam-dickinson', // author id url
        date: 'April 11, 2026',
        category: 'Client Relief', // shows up in the little blug tag
        image: '', // optional image that shows between title and tag; format
        summary: 'The firm obtains $50,000.00 settlement for defective painting services rendered by Maaco Auto Body', // shows up on the main blog page
        body: [ // separate paragraphs into different strings ''
            'On April 8th, 2024, the firm’s Client brought their classic 1969 Pontiac GTO to Maaco Auto Body Temecula to restore it to its former glory. At the time, its paint and hood were severely weathered, requiring sanding and professional paint job to look beautiful once more.',
            'The auto-body shop was informed that the battery and electrical system in the vehicle were faulty so that when the vehicle was not running, the battery would have to be disconnected or else it would lose its charge. Maaco assured the Client they would take proper care of this. The full resurfacing and painting of the Pontiac was for the agreed-upon contract price of approximately $4,500.',
            'On April 26th, 2024, Maaco reassured the Client that the service would be completed by the following Monday. That day, the Client took off work and was halfway to the auto body shop before receiving a call saying that Maaco would not be able to complete the job that day. The Client decided to stop by the shop to check on the progress. While there, the Client discovered that their car had been absolutely mangled, with its hood completely dented and tossed aside on the ground. At the same time, the interior of the Pontiac was filthy due to the shop erroneously leaving the vehicle’s windows down, allowing metal rust particles and dust from sanding other nearby vehicles to coat the seats of the Pontiac.',
            'The owner of the Pontiac was furious at this discovery, but Maaco assured they would repair the dented hood and restore it to its former status. On subsequent checks of the project, it was also revealed that Maaco had not been disconnecting the battery, leading to it dying and a replacement one being needed. On top of this, the locking mechanism to the vehicle’s door was damaged and needed refurbishing. This showed a clear lack of care and defective services being rendered by Maaco.',
            'After Maaco represented that they completed the job, the Client discovered nothing more than a subpar quality paint job with scratches and missing paint covering the vehicle. It was also apparent that the vehicle’s hood was never fully repaired, leaving an unsightly gap at the front of the car.',
            'These events were presented to Consumer Equity Legal who took the case on, citing violations of the Consumer Legal Remedies Act, Fraudulent and Negligent Misrepresentations, Breach of Contract, Conversion, Civil Theft, and Unjust Enrichment against Maaco and its owner.',
            'After filing a civil complaint and working through discovery with Maaco, Consumer Equity Legal negotiated a settlement of $50,000.00 for the case which Maaco agreed to pay to resolve the claims.',
        ],
    },
];

function BlogPost() {
    const { postId } = useParams<{ postId: string }>();
    const post = blogPosts.find(p => p.id === postId);

    if (!post) {
        return <Navigate to="/404" replace />;
    }

    const authorMember = teamMembers.find(m => m.id === post.authorId);

    return (
        <div className="blog-post-container">
            {/* Header */}
            <section className="blog-post-header">
                <span className="blog-post-category">{post.category}</span>
                {post.image && (
                    <img
                        src={post.image}
                        alt={post.title}
                        className="blog-post-hero-image"
                    />
                )}
                <h1 className="blog-post-title">{post.title}</h1>
                <p className="blog-post-date">{post.date}</p>
            </section>

            {/* Body */}
            <section className="blog-post-body">
                {post.body.map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                ))}
            </section>

            {/* Author Card */}
            <section className="blog-post-author-card">
                {authorMember?.image && (
                    <img
                        src={authorMember.image}
                        alt={post.author}
                        className="author-card-photo"
                    />
                )}
                <div className="author-card-info">
                    <div className="author-card-label">Written by</div>
                    <div className="author-card-name">{post.author}</div>
                    <Link to={`/team/${post.authorId}`} className="author-card-link">
                        View Profile →
                    </Link>
                </div>
            </section>

            {/* Back to Blog */}
            <div className="blog-post-back">
                <Link to="/blog" className="back-link">← Back to Blog</Link>
            </div>
        </div>
    );
}

export default BlogPost;
